import queryString from 'query-string'
import PCLNAnalytics from '@pcln/analytics'
import { customHistory } from './routerHistory'
import { clientErrorRedirect } from '../../helpers/clientErrorRedirect'

const SLICES_ROOT = '/m/fly/search/:slices(\\w{3}-\\w{3}-\\d{8})+'

class RoutingHelper {
  constructor(history = customHistory) {
    this.history = history
  }

  static routePaths = {
    mWebDetails: [`${SLICES_ROOT}/modal-details/:id`],
    filters: [`${SLICES_ROOT}/filter`],
    listings: [SLICES_ROOT],
  }

  /**
   * Returns true if instance has a valid browser history object
   * @returns {boolean}
   * @private
   */
  __checkHistory = () => {
    if (!this.history || !this.history.location) {
      console.error(
        'RoutingHelper was not initialized with a browser history. Skipping transition.'
      ) // eslint-disable-line
      return false
    }

    return true
  }

  __getSlicesURLRoot = () => {
    // Remove non-slice nodes from pathname
    const regex = /^\S*\/m\/fly\/search\/(([a-z]+\/)?)(?:\w{3}-\w{3}-\d{8}\/)+/g
    const found = this.history.location.pathname.match(regex)

    return found ? found[0] : null
  }

  /**
   * Transition from mWeb details back to listings page
   */
  goToListings = () => {
    if (!this.__checkHistory()) return

    // parse query params
    const detailsParams = queryString.parse(this.history.location.search)

    // Remove non-listings query params
    Object.assign(detailsParams, {
      upsell: undefined,
    })

    const listingsPathname = this.__getSlicesURLRoot()

    if (listingsPathname) {
      // navigate to listings with same query params
      this.history.push({
        pathname: listingsPathname,
        search: queryString.stringify(detailsParams),
      })
    } else {
      const message = `Error navigating to Listings from ${this.history.location.pathname}`
      PCLNAnalytics.logError({
        message,
      })
      // new relic error
      window?.newrelic?.noticeError(message)
      clientErrorRedirect()
    }
  }

  /**
   * Navigate from Listings to Filter route
   */
  goToFilters = () => {
    if (!this.__checkHistory()) return

    // parse query params
    const params = queryString.parse(this.history.location.search)

    const listingsPathname = this.__getSlicesURLRoot()

    if (listingsPathname) {
      // navigate to listings with same query params
      this.history.push({
        pathname: `${listingsPathname}filter`,
        search: queryString.stringify(params),
      })
    } else {
      const message = `Error navigating to Filters from ${this.history.location.pathname}`
      PCLNAnalytics.logError({
        message,
      })
      // new relic error
      window?.newrelic?.noticeError(message)
      clientErrorRedirect()
    }
  }

  /**
   * Transition from listings to mWeb details
   * @param itinId
   */
  goToMobileDetails = (itinId, refId, isUpsell) => {
    if (!this.__checkHistory()) return

    // parse query params from search URL
    const searchParams = queryString.parse(this.history.location.search)

    // add additional params for details page
    const detailsParams = Object.assign({}, searchParams, {
      'allow-share': true,
      'ref-id': refId,
      upsell: isUpsell || undefined,
    })

    // remove trailing slash on search URL
    let searchUrl = this.history.location.pathname

    if (searchUrl.charAt(searchUrl.length - 1) === '/') {
      searchUrl = searchUrl.substring(0, searchUrl.length - 1)
    }

    this.history.push({
      pathname: `${searchUrl}/modal-details/${itinId}`,
      search: queryString.stringify(detailsParams),
    })
  }

  /**
   * Get a route with a white label path code
   * @param route
   * @param pathCode
   */
  getRouteWithPathCode = (route, pathCode) => {
    const baseName = '/m/fly/search'
    const slicesRegex = '/:slices(\\w{3}-\\w{3}-\\d{8})+'
    const routePaths = {
      filters: 'filter',
      listings: '',
    }

    return [`${baseName}${`/${pathCode}`}${slicesRegex}/${routePaths[route]}`]
  }
}

export { RoutingHelper }
